import { Button, Grid2, Paper, TextField } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { AssetsFilter } from "../../../../../controls/asset-filter/AssetFilter";
import { ShiftSelector } from "../../../../../controls/shift-selector/ShiftSelector";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import { TimeService, getMomentTz } from "../../../../../services";
import { ContentInput } from "../../../entry/components/entry-viewer/ContentInput";
import { ENTRY_INITIAL_VALUES, Entry } from "../../../entry/models/Entry";

const moment = getMomentTz();

type Props = {
	entry: Entry | null;
	show: "edit" | "view" | "";
	onChangeShow: () => void;
	onHide: () => void;
	filterAssets?: "area";
	showAsset?: boolean;
	showAttachments?: boolean;
};

export const EntryViewer = ({
	entry,
	show,
	onHide,
	filterAssets,
	showAsset,
	showAttachments = false,
}: Props) => {
	const [error, setError] = useState("");

	const { control, setValue, reset, watch, getValues } = useForm<Entry>();

	const content = watch("Content");
	const updatedBy = watch("UpdatedBy");
	const updateTimestamp = watch("UpdateTimestamp");
	const entryId = watch("EntryId");
	const title = watch("Title");
	const author = watch("Author");
	const shiftId = watch("ShiftId");
	const assetId = watch("AssetId");
	const createTimestamp = watch("CreateTimestamp");

	useEffect(() => {
		reset(entry || ENTRY_INITIAL_VALUES);
	}, [entry, show]);

	return (
		<>
			{entry?.EntryId && (
				<HelmetDexteel
					title={`'${entry?.Title}' by ${entry.Author} - Logbook Report`}
				/>
			)}
			{entry?.EntryId ? (
				<Paper
					elevation={1}
					sx={{
						width: "100%",
						height: "100%",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Grid2
						container
						spacing={1}
						component={"form"}
						height={"100%"}
						flexDirection={"column"}
						flexWrap={"nowrap"}
						p={1}
					>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="Title"
								control={control}
								render={({ field: { value } }) => (
									<TextField
										label="Title"
										value={value ?? ""}
										size="medium"
										disabled
									/>
								)}
							/>
						</Grid2>
						<Grid2 container size={{ xs: 12 }} spacing={2}>
							<Grid2 size={{ xs: 12, md: 3 }}>
								<Controller
									name="CreateTimestamp"
									control={control}
									render={({ field: { value } }) => (
										<DateTimePicker
											label="Creation Time"
											timezone={TimeService.getInstance().getServerTimeZone()}
											format="MM/DD/YYYY HH:mm"
											value={value ? moment(value) : null}
											disabled
											slotProps={{
												textField: {
													variant: "outlined",
													size: "small",
													fullWidth: true,
													InputLabelProps: {
														shrink: true,
													},
												},
											}}
										/>
									)}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12, md: 3 }}>
								<Controller
									name="ShiftId"
									control={control}
									render={({ field }) => (
										<ShiftSelector
											value={field.value as number | null}
											disabled
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, md: showAsset ? 2 : 6 }}>
								<Controller
									name="Author"
									control={control}
									rules={{
										required: "Author is required",
									}}
									render={({ field: { value }, fieldState: { error } }) => (
										<TextField
											label="Author"
											variant="outlined"
											value={value ?? ""}
											fullWidth
											disabled
											size="small"
										/>
									)}
								/>
							</Grid2>
							{showAsset ? (
								<Grid2 size={{ md: 2, xs: 12 }}>
									<Controller
										name="AssetId"
										control={control}
										render={({ field }) => (
											<AssetsFilter
												value={field.value as number | null}
												onChange={field.onChange}
												filterAreaAssets={filterAssets === "area"}
												disabled
											/>
										)}
									/>
								</Grid2>
							) : null}
						</Grid2>
						<Grid2
							size={{ md: 12 }}
							height={"100%"}
							flexGrow={1}
							overflow="auto"
						>
							<Controller
								name="Content"
								control={control}
								render={({ field: { value, onChange } }) => (
									<ContentInput
										UpdatedBy={updatedBy as string}
										UpdateTimestamp={updateTimestamp as Date}
										onChange={(val) => setValue("Content", val)}
										value={content as string}
										isEditing={false}
										isNewEntry={false}
										entryId={entryId as number}
										entryData={{
											assetId: assetId as number,
											entryId: entryId as number,
											shiftId: shiftId as number,
											title: title as string,
											content: content as string,
											author: author as string,
											createTimestamp: createTimestamp as Date,
										}}
										onEntrySubmitted={undefined}
										allowAttachments={showAttachments}
									/>
								)}
							/>
						</Grid2>
						<Grid2 container size={{ xs: 12 }}>
							<Grid2 container size={{ md: 6, xs: 12 }} spacing={1}>
								<Grid2 size={{ md: 6, xs: 12 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={() => onHide()}
									>
										Close
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				</Paper>
			) : null}
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
